import { coverageById, type FormVisibility } from '@cod/config-schema';
import {
  Alert,
  Box,
  Button,
  Card,
  Divider,
  IconButton,
  Link,
  Tag,
  Text,
  Thumbnail,
  Title,
} from '@nimbus-ds/components';
import {
  ArrowRightIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  CloseIcon,
  ExternalLinkIcon,
} from '@nimbus-ds/icons';
import { Page } from '@nimbus-ds/patterns';
import { Fragment, useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { getDashboard, type DashboardData } from '../api/client.js';
import { LOGO_URL } from '../components/Shell.js';
import { ControlledToggle } from '../components/controls.js';
import { ChecklistSkeleton, FunnelSkeleton } from '../components/skeletons.js';
import { EmptyState } from '../components/states.js';
import type { Route } from '../routes.js';
import { useAppState } from '../state/AppState.js';
import {
  activePixelCount,
  formatOrderTime,
  hasCustomShippingRates,
  isFormCustomized,
} from './dashboard.model.js';
import { overlappingProductNames } from './quantityOffers.model.js';

/** Reuses the Settings page's visibility labels so both screens name the same state identically. */
const VISIBILITY_LABEL: Record<FormVisibility, string> = {
  disabled: 'settings.visDisabled',
  cart: 'settings.visCart',
  product: 'settings.visProduct',
  both: 'settings.visBoth',
};

/**
 * A Nimbus Card's surface, rebuilt on `Box`.
 *
 * 🚨 `Card` exposes only `padding` and `backgroundColor` — it has no height, and its own CSS is
 * `width: 100%` with no stretch. So inside a wrapping row it never fills its flex item: a card
 * whose value wraps to two lines ("Both cart and product pages") grows, its neighbours do not,
 * and every action ends up at a different vertical position.
 *
 * `Box` carries the same tokens Nimbus builds `card_container` from — border-radius 2, shadow
 * level 2, spacing-4 padding, `neutral-background` — AND it can be the flex item itself. So the
 * row's default `align-items: stretch` reaches the content, every card in a line is the same
 * height, and `justifyContent: space-between` pins the last child to the bottom.
 */
function CardSurface({ children }: { children: ReactNode }): JSX.Element {
  return (
    <Box
      flex="1 1 170px"
      minWidth="160px"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      gap="2"
      padding="4"
      backgroundColor="neutral-background"
      borderRadius="2"
      boxShadow="2"
    >
      {children}
    </Box>
  );
}

/**
 * Share of the previous funnel stage that reached this one.
 *
 * Null when the previous stage is 0: "0 of 0" is not a rate, and printing 0% there would report
 * a conversion failure the store has not had yet. The connector still draws, so the chain keeps
 * its shape.
 *
 * 🚨 Deliberately a ratio between two numbers that are both on screen, never a share of a goal.
 * These are lifetime counts with no target to be a percentage OF — which is why this card draws
 * no bars: a filled track reads as progress toward something, and there is nothing.
 */
function conversionLabel(current: number, previous: number, locale: string): string | null {
  if (previous <= 0) return null;
  return (current / previous).toLocaleString(locale, {
    style: 'percent',
    maximumFractionDigits: 1,
  });
}

/**
 * The link between two funnel stages: the conversion as a pill, then an arrow.
 *
 * The rate is the one number on this card a merchant can act on — a count is what it is, but a
 * collapsing conversion is a problem to go fix — so it carries a filled `Tag` rather than the
 * muted caption it started as, and it stays legible against the large counts either side.
 *
 * `paddingTop` lifts the whole link onto the numbers' line rather than the labels' — the stages
 * are top-aligned so that labels of one and two lines still start together, which leaves the
 * connector floating at the top of the row without it.
 *
 * It takes an equal share of the row (`flex: 1 1 0%`) and centres itself in it, exactly like a
 * stage. Content-sized, it hugged the pill up against the stage on its left and pointed its arrow
 * into open space; sharing the row evenly puts the same gap either side of every connector, which
 * is what makes the five items read as one chain spanning the card.
 */
function FunnelLink({ rate }: { rate: string | null }): JSX.Element {
  return (
    <Box
      flex="1 1 0%"
      display="flex"
      alignItems="center"
      justifyContent="center"
      gap="2"
      paddingTop="1"
    >
      {rate && <Tag appearance="primary">{rate}</Tag>}
      <Box display="flex" color="neutral-textLow">
        <ArrowRightIcon size={12} />
      </Box>
    </Box>
  );
}

/**
 * One "what's live right now" card: a current setting, and a way to go change it.
 *
 * The value is plain text, never a link. It is DATA — colouring it like navigation makes five
 * cards of settings read as five links, and it stops matching the metric cards further down,
 * which show their values the same way. Only the small action underneath is coloured.
 */
function LiveCard({
  label,
  value,
  action,
  onClick,
}: {
  label: string;
  value: string;
  action: string;
  onClick: () => void;
}): JSX.Element {
  return (
    <CardSurface>
      <Box display="flex" flexDirection="column" gap="1">
        <Text fontSize="caption" color="neutral-textLow">
          {label}
        </Text>
        <Title as="h4">{value}</Title>
      </Box>
      {/* Its own row, so `space-between` on the surface pins it to the bottom edge and every
          card's action lines up regardless of how many lines the value took. */}
      <Box display="flex">
        <Link
          as="button"
          appearance="primary"
          textDecoration="none"
          fontSize="caption"
          onClick={onClick}
        >
          {action}
          <ChevronRightIcon size="small" />
        </Link>
      </Box>
    </CardSurface>
  );
}

/** Format money with the store currency; falls back to a plain 2-decimal number (as Analytics). */
function formatMoney(value: number, currency: string | null, locale: string): string {
  if (currency) {
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(value);
    } catch {
      /* invalid currency code — fall through to plain formatting */
    }
  }
  return value.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

interface SetupStep {
  key: string;
  done: boolean;
  /** Not needed to take a COD order; marked so the checklist never feels unfinishable. */
  optional?: boolean;
  /** Absent when there is nothing left to do — "Activate the app" once it is already on, whose
   *  only control is the toggle directly above the checklist. Every other step keeps its link
   *  after completion, so the merchant can go back and change what they set. */
  onAction?: () => void;
}

export function Dashboard({ onNavigate }: { onNavigate: (route: Route) => void }): JSX.Element {
  const { t, i18n } = useTranslation();
  const { analytics, config, settings, saveSettings } = useAppState();
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  // Gates the two sections that need order data. Without it the checklist paints "5 of 6" and
  // then silently corrects itself to "6 of 6" once the orders land — a progress number the
  // merchant watches change is worse than one that arrives a beat later.
  const [loading, setLoading] = useState(true);
  // null = follow the checklist's own completion; a boolean is the merchant's explicit choice.
  const [checklistOpen, setChecklistOpen] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await getDashboard();
        if (!cancelled) setDashboard(data);
      } catch {
        /* non-critical: the page still renders the config-derived half */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const locale = i18n.language;
  const appEnabled = settings?.app_enabled ?? false;
  const lifetimeRevenue = dashboard?.lifetimeRevenue ?? 0;
  const recentOrders = dashboard?.recentOrders ?? [];
  const enabledOffers = (config?._future.quantity_offers ?? []).filter((o) => o.enabled);
  const activePixels = config ? activePixelCount(config) : 0;

  // "No activity yet" — not "no analytics object". A loaded summary of all zeros is the case that
  // matters, and it is the one the `?? 0` fallbacks used to hide. Revenue counts too: a store can
  // have orders while its funnel events are blocked, and three zeros would then be a lie.
  //
  // `form_submitted` still counts here even though the card no longer draws it: it is a real
  // buyer action, and a store whose submissions all failed to become orders has activity to show.
  const noActivity =
    (!analytics ||
      (analytics.form_viewed ?? 0) +
        (analytics.form_opened ?? 0) +
        (analytics.form_submitted ?? 0) +
        (analytics.order_created ?? 0) ===
        0) &&
    lifetimeRevenue === 0 &&
    recentOrders.length === 0;

  // Only one offer applies per product on the storefront, so two ENABLED offers on the same
  // product means the lower-priority one silently never shows. Disabled offers can't collide.
  const offerConflicts = overlappingProductNames(enabledOffers);

  // The lifetime funnel, in the order a buyer walks it, each stage carrying its conversion from
  // the one before. Chained rather than shown as separate cards: the drop-off between stages is
  // the only thing these counts are useful for, and numbers side by side never showed it.
  //
  // 🚨 `form_submitted` is deliberately NOT a stage, for two reasons. The legacy bundle fires it
  // at the top of `onSubmit`, immediately before the order POST, so it differed from
  // `order_created` only by the submissions that ERRORED — a near-100% link the merchant could do
  // nothing with, sitting between the two rates that matter. And the NubeSDK bundle never fires it
  // at all (`storefront-app/src/main.ts` tracks only `form_viewed` / `form_opened`), so on a
  // Patagonia store the stage read 0 with orders after it and the final pill vanished entirely.
  // Dropping it makes the last pill orders-per-open, which is the number a COD store is judged on.
  // The event is still recorded and still counts as activity above.
  const funnelCounts = [
    { key: 'formViews', count: analytics?.form_viewed ?? 0 },
    { key: 'formOpens', count: analytics?.form_opened ?? 0 },
    { key: 'ordersCreated', count: analytics?.order_created ?? 0 },
  ];
  const funnel = funnelCounts.map((stage, index) => ({
    ...stage,
    conversion:
      index === 0 ? null : conversionLabel(stage.count, funnelCounts[index - 1]!.count, locale),
  }));

  // Divided by the order count the chain ends on, not by a separate order-table count, so a
  // merchant who checks the arithmetic on screen gets the number we printed. The two agree in
  // practice — `order.service.ts` writes the `Order` row and the `order_created` event in the
  // same code path — but only this pairing is guaranteed to.
  const orderCount = analytics?.order_created ?? 0;
  const avgOrder = lifetimeRevenue > 0 && orderCount > 0 ? lifetimeRevenue / orderCount : null;

  const steps: SetupStep[] = [
    {
      key: 'activate',
      done: appEnabled,
      ...(appEnabled ? {} : { onAction: () => void saveSettings({ app_enabled: true }) }),
    },
    {
      key: 'customize',
      done: config ? isFormCustomized(config) : false,
      onAction: () => onNavigate('designer'),
    },
    {
      key: 'shipping',
      done: config ? hasCustomShippingRates(config) : false,
      onAction: () => onNavigate('shipping-rates'),
    },
    {
      key: 'offer',
      done: enabledOffers.length > 0,
      optional: true,
      onAction: () => onNavigate('quantity-offers'),
    },
    {
      key: 'pixel',
      done: activePixels > 0,
      optional: true,
      onAction: () => onNavigate('tracking'),
    },
    {
      key: 'order',
      done: recentOrders.length > 0,
      onAction: () => onNavigate('analytics'),
    },
  ];
  const completed = steps.filter((s) => s.done).length;
  const allDone = completed === steps.length;
  const showChecklistBody = checklistOpen ?? !allDone;

  return (
    <Page maxWidth="1000px">
      <Page.Body>
        <Box display="flex" flexDirection="column" gap="4">
          {/* 1. Welcome banner. Dismissal is stored on the store row, not in the browser, so it
              follows the merchant across devices. */}
          {settings && !settings.welcome_dismissed && (
            <Card>
              <Card.Body>
                <Box display="flex" gap="3" alignItems="flex-start">
                  <Box flex="0 0 auto" paddingTop="1">
                    <Thumbnail src={LOGO_URL} alt={t('nav.appName')} width="40px" />
                  </Box>
                  <Box display="flex" flexDirection="column" gap="2" flex="1">
                    <Title as="h3">{t('dashboard.welcomeTitle')}</Title>
                    <Text color="neutral-textLow">{t('dashboard.welcomeBody')}</Text>
                    <Box display="flex" gap="2" alignItems="center" flexWrap="wrap">
                      <Button appearance="primary" onClick={() => onNavigate('designer')}>
                        {t('dashboard.welcomeCta')}
                      </Button>
                    </Box>
                  </Box>
                  <IconButton
                    size="2rem"
                    title={t('common.close')}
                    source={<CloseIcon />}
                    onClick={() => void saveSettings({ welcome_dismissed: true })}
                  />
                </Box>
              </Card.Body>
            </Card>
          )}

          {/* 5. Proactive warnings. Above the fold on purpose: each of these is a live blocker the
              merchant would otherwise discover from a failed order or a buyer complaint. */}
          {dashboard?.missingScopes.map((scope) => (
            <Alert key={scope} appearance="danger" title={t(`dashboard.scope.${scope}.title`)}>
              <Box display="flex" flexDirection="column" gap="2" alignItems="flex-start">
                <Text>{t(`dashboard.scope.${scope}.body`)}</Text>
                {/* Re-authorizing is the ONLY way a scope ticked in the Partner Portal reaches an
                    already-installed store. Opens top-level: the grant screen cannot run in the
                    admin iframe. */}
                <Link
                  as="a"
                  href={dashboard.authorizeUrl}
                  target="_blank"
                  appearance="primary"
                  textDecoration="none"
                >
                  {t('dashboard.scopeFix')}
                  <ExternalLinkIcon size="small" />
                </Link>
              </Box>
            </Alert>
          ))}

          {offerConflicts.length > 0 && (
            <Alert appearance="warning" title={t('dashboard.offerConflictTitle')}>
              <Box display="flex" flexDirection="column" gap="2" alignItems="flex-start">
                {offerConflicts.map((name) => (
                  <Text key={name}>{t('dashboard.offerConflictBody', { product: name })}</Text>
                ))}
                <Link
                  as="button"
                  appearance="primary"
                  textDecoration="none"
                  onClick={() => onNavigate('quantity-offers')}
                >
                  {t('dashboard.offerConflictFix')}
                </Link>
              </Box>
            </Alert>
          )}

          {/* Master activate/deactivate switch for the whole app. */}
          <Card>
            <Card.Body>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box display="flex" flexDirection="column" gap="1">
                  <Title as="h4">{t('dashboard.appStatus')}</Title>
                  <Text fontSize="caption" color="neutral-textLow">
                    {t('dashboard.appStatusHelp')}
                  </Text>
                </Box>
                <ControlledToggle
                  name="app-enabled"
                  active={appEnabled}
                  onChange={(next) => void saveSettings({ app_enabled: next })}
                />
              </Box>
            </Card.Body>
          </Card>

          {/* 2. Setup checklist. Collapses itself once every step is done, so an activated store
              gets its dashboard back instead of a permanent wall of ticks. */}
          {loading ? (
            // `steps.length`, not a fixed count: the skeleton draws exactly the rows about to
            // appear, so the card does not resize as the checklist replaces it.
            <ChecklistSkeleton steps={steps.length} />
          ) : (
            <Card>
              <Card.Body>
                <Box display="flex" flexDirection="column" gap="3">
                  <Box display="flex" alignItems="center" justifyContent="space-between" gap="2">
                    <Box display="flex" flexDirection="column" gap="1">
                      <Title as="h4">
                        {allDone ? t('dashboard.setupDoneTitle') : t('dashboard.setupTitle')}
                      </Title>
                      <Text fontSize="caption" color="neutral-textLow">
                        {t('dashboard.setupProgress', { done: completed, total: steps.length })}
                      </Text>
                    </Box>
                    <Button
                      appearance="transparent"
                      onClick={() => setChecklistOpen(!showChecklistBody)}
                    >
                      {showChecklistBody ? (
                        <ChevronUpIcon size="small" />
                      ) : (
                        <ChevronDownIcon size="small" />
                      )}
                      {showChecklistBody ? t('dashboard.setupHide') : t('dashboard.setupShow')}
                    </Button>
                  </Box>

                  {showChecklistBody && (
                    <Box display="flex" flexDirection="column">
                      {steps.map((step, index) => (
                        <Fragment key={step.key}>
                          {index > 0 && <Divider />}
                          <Box display="flex" alignItems="center" gap="3" paddingY="3">
                            <Box
                              display="flex"
                              color={step.done ? 'success-textLow' : 'neutral-interactive'}
                            >
                              <CheckCircleIcon size="medium" />
                            </Box>
                            <Box display="flex" flexDirection="column" gap="1" flex="1">
                              <Box display="flex" gap="2" alignItems="center" flexWrap="wrap">
                                <Text
                                  fontWeight="medium"
                                  color={step.done ? 'neutral-textLow' : 'neutral-textHigh'}
                                >
                                  {t(`dashboard.steps.${step.key}.title`)}
                                </Text>
                                {step.optional && (
                                  <Tag appearance="neutral">{t('dashboard.stepOptional')}</Tag>
                                )}
                              </Box>
                              <Text fontSize="caption" color="neutral-textLow">
                                {t(`dashboard.steps.${step.key}.help`)}
                              </Text>
                            </Box>
                            {/* Shown on done steps too: "each step links to the screen that
                              completes it" stays true after completion, and a merchant who
                              finished a step still needs the way back to change it. */}
                            {step.onAction && (
                              <Link
                                as="button"
                                appearance={step.done ? 'neutral' : 'primary'}
                                textDecoration="none"
                                fontSize="caption"
                                onClick={step.onAction}
                              >
                                {t(`dashboard.steps.${step.key}.action`)}
                                <ChevronRightIcon size="small" />
                              </Link>
                            )}
                          </Box>
                        </Fragment>
                      ))}
                    </Box>
                  )}
                </Box>
              </Card.Body>
            </Card>
          )}

          {/* 3. What's live right now — the saved configuration, so the merchant does not have to
              open two screens to know what buyers currently see. */}
          {config && settings && (
            <Box display="flex" flexDirection="column" gap="2">
              <Title as="h4">{t('dashboard.liveTitle')}</Title>
              <Box display="flex" flexWrap="wrap" gap="4">
                <LiveCard
                  label={t('dashboard.liveVisibility')}
                  value={t(VISIBILITY_LABEL[settings.visibility])}
                  action={t('dashboard.liveChange')}
                  onClick={() => onNavigate('settings')}
                />
                {/* Hidden when the app is switched off for the storefront: with `visibility:
                    'disabled'` nothing renders anywhere, so naming the mode buyers would get
                    describes a surface that does not exist. Every other visibility keeps it —
                    the value is the merchant's saved choice and is reported as saved. */}
                {settings.visibility !== 'disabled' && (
                  <LiveCard
                    label={t('dashboard.liveMode')}
                    value={t(
                      config.mode === 'popup' ? 'dashboard.modePopup' : 'dashboard.modeEmbedded',
                    )}
                    action={t('dashboard.liveChange')}
                    onClick={() => onNavigate('designer')}
                  />
                )}
                <LiveCard
                  label={t('dashboard.liveOffers')}
                  value={enabledOffers.length.toLocaleString(locale)}
                  action={t('dashboard.liveChange')}
                  onClick={() => onNavigate('quantity-offers')}
                />
                <LiveCard
                  label={t('dashboard.livePixels')}
                  value={activePixels.toLocaleString(locale)}
                  action={t('dashboard.liveChange')}
                  onClick={() => onNavigate('tracking')}
                />
                <LiveCard
                  label={t('dashboard.liveCoverage')}
                  value={coverageById(config.coverage.id)?.label ?? t('dashboard.liveCoverageOff')}
                  action={t('dashboard.liveChange')}
                  onClick={() => onNavigate('designer')}
                />
              </Box>
            </Box>
          )}

          {/**
           * 4. The lifetime counters. 🚨 The initial state matters: every metric reads
           * `analytics?.x ?? 0`, so a store that has never been used rendered confident zeros —
           * indistinguishable from a store whose form is broken. The empty message says which.
           *
           * No action: this one is waiting on buyers, not on the merchant, so offering a button
           * would be pointing at nothing.
           */}
          {loading ? (
            <FunnelSkeleton />
          ) : noActivity ? (
            <EmptyState title={t('states.noDataTitle')} text={t('states.noDataText')} />
          ) : (
            <>
              <Card>
                <Card.Body>
                  <Box display="flex" flexDirection="column" gap="4">
                    <Title as="h4">{t('dashboard.performanceTitle')}</Title>

                    {/* Stages top-aligned so a label that wraps to two lines does not drag its
                        number down out of line with the rest of the chain.

                        Two rules make the chain fill the card evenly, and both are needed:

                        - `flex: 1 1 0%` (not `auto`) gives every stage an EQUAL share of the row.
                          Sized to their content, a short count and a long label made columns of
                          wildly different widths, so the connectors landed at uneven intervals.
                        - every item centres its own content, connectors included. Equal columns
                          alone still left each number pinned to its column's left edge, which
                          pooled the chain on the left and left the last third of the card empty.
                          Centred, the five items sit at the middles of five even columns, so the
                          stats spread to both edges and each arrow has equal air either side. */}
                    <Box display="flex" flexWrap="wrap" alignItems="flex-start" gap="3">
                      {funnel.map((stage, index) => (
                        <Fragment key={stage.key}>
                          {index > 0 && <FunnelLink rate={stage.conversion} />}
                          <Box
                            flex="1 1 0%"
                            minWidth="80px"
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            textAlign="center"
                            gap="1"
                          >
                            <Title as="h3">{stage.count.toLocaleString(locale)}</Title>
                            <Text fontSize="caption" color="neutral-textLow" textAlign="center">
                              {t(`dashboard.${stage.key}`)}
                            </Text>
                          </Box>
                        </Fragment>
                      ))}
                    </Box>

                    {/* On its own tinted panel rather than one more line of text under a rule:
                        revenue is the funnel's outcome, not a fifth stage, and it is the number
                        the merchant opens this page for. Green because it is money earned — the
                        same reading the Analytics celebration banner gives it, which shows this
                        exact figure (both sum created orders' `total_amount`). */}
                    <Box
                      backgroundColor="success-surface"
                      borderRadius="2"
                      padding="4"
                      display="flex"
                      flexWrap="wrap"
                      alignItems="center"
                      justifyContent="space-between"
                      gap="2"
                    >
                      <Box display="flex" flexDirection="column" gap="1">
                        <Text fontSize="caption" color="success-textLow">
                          {t('dashboard.lifetimeRevenue')}
                        </Text>
                        <Title as="h2" color="success-textHigh">
                          {lifetimeRevenue > 0
                            ? formatMoney(lifetimeRevenue, dashboard?.currency ?? null, locale)
                            : t('dashboard.noOrdersYet')}
                        </Title>
                      </Box>
                      {avgOrder != null && (
                        <Tag appearance="success">
                          {t('dashboard.avgOrder', {
                            amount: formatMoney(avgOrder, dashboard?.currency ?? null, locale),
                          })}
                        </Tag>
                      )}
                    </Box>
                  </Box>
                </Card.Body>
              </Card>

              {recentOrders.length > 0 && (
                <Card>
                  <Card.Header title={t('dashboard.recentOrders')} />
                  <Card.Body>
                    <Box display="flex" flexDirection="column">
                      {recentOrders.map((order, index) => (
                        <Fragment key={order.id}>
                          {index > 0 && <Divider />}
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            gap="2"
                            paddingY="2"
                          >
                            <Box display="flex" flexDirection="column" gap="1">
                              <Text fontWeight="medium">
                                {order.number != null ? `#${order.number}` : `#${order.id}`}
                              </Text>
                              <Text fontSize="caption" color="neutral-textLow">
                                {formatOrderTime(order.createdAt, locale)}
                              </Text>
                            </Box>
                            <Text fontWeight="medium">
                              {formatMoney(order.total, order.currency, locale)}
                            </Text>
                          </Box>
                        </Fragment>
                      ))}
                    </Box>
                  </Card.Body>
                  <Card.Footer>
                    {/* The merchant's real order list lives in the Nuvemshop admin, not in this
                        app. New tab, because we are inside the admin's iframe. Falls back to our
                        Analytics page when the store domain was never captured at install. */}
                    {dashboard?.ordersAdminUrl ? (
                      <Link
                        as="a"
                        href={dashboard.ordersAdminUrl}
                        target="_blank"
                        appearance="primary"
                        textDecoration="none"
                      >
                        {t('dashboard.viewAllOrders')}
                        <ExternalLinkIcon size="small" />
                      </Link>
                    ) : (
                      <Link
                        as="button"
                        appearance="primary"
                        textDecoration="none"
                        onClick={() => onNavigate('analytics')}
                      >
                        {t('dashboard.viewAllOrders')}
                      </Link>
                    )}
                  </Card.Footer>
                </Card>
              )}
            </>
          )}
        </Box>
      </Page.Body>
    </Page>
  );
}
